import { Form, Modal, Radio, Select, Typography } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { StatusesEditableByBulk } from 'business/quality-control/pages/segment-history/components/add-manuel-scan-modal/types';
import { useBulkStatusUpdate } from 'business/quality-control/pages/segment-history/components/bulk-status-modal/use-bulk-status-update';
import {
  Module_Enum,
  useGetSegmentHistoryOptionsQuery,
} from 'generated/graphql';
import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';

const UpdateMode = {
  complete: 'complete',
  partial: 'partial',
} as const;

type UpdateModeValue = (typeof UpdateMode)[keyof typeof UpdateMode];

interface FormValues {
  updateMode: UpdateModeValue;
  newStatus: Quality_SegmentHistoryStatusEnum_Enum;
  scanSiteId: string;
}

export const BulkStatusModal = ({
  open,
  onClose,
  total,
  onSuccess,
  onStartPartialSelection,
}: {
  open: boolean;
  onClose: () => void;
  total: number;
  onSuccess: () => void;
  onStartPartialSelection: (values: {
    newStatus: Quality_SegmentHistoryStatusEnum_Enum;
    scanSiteId: string;
  }) => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const updateMode = Form.useWatch('updateMode', form) ?? UpdateMode.complete;

  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetSegmentHistoryOptionsQuery({
    variables: {
      constructionSiteId: currentConstructionSite.id,
      projectId: currentConstructionSite.projectId,
    },
    context: { headers: graphqlHeaders },
    skip: !open,
  });

  const scanSiteOptions =
    data?.scanSites
      ?.filter((site) => site.isReceiptActivated)
      .map((site) => ({
        label: site.label,
        value: site.id,
      })) ?? [];

  const statusOptions = StatusesEditableByBulk.map((status) => ({
    value: status,
    label: t(`qualityControl.segmentHistory.status.state`, { context: status }),
  }));

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const { submit, isPending } = useBulkStatusUpdate({
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
  });

  const handleOk = () => form.submit();

  const handleFinish = (values: FormValues) => {
    if (values.updateMode === UpdateMode.partial) {
      onStartPartialSelection({
        newStatus: values.newStatus,
        scanSiteId: values.scanSiteId,
      });
      handleClose();
      return;
    }

    submit({
      newStatus: values.newStatus,
      scanSiteId: values.scanSiteId,
    });
  };

  return (
    <Modal
      title={t('qualityControl.segmentHistory.bulkStatusModal.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleClose}
      okText={
        updateMode === UpdateMode.partial
          ? t('qualityControl.segmentHistory.bulkStatusModal.continueButton')
          : undefined
      }
      okButtonProps={{ loading: isPending }}
      destroyOnHidden
    >
      <Typography.Paragraph>
        {t('qualityControl.segmentHistory.bulkStatusModal.impactedSegments', {
          count: total,
        })}
      </Typography.Paragraph>
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ updateMode: UpdateMode.complete }}
      >
        <Form.Item<FormValues>
          name="updateMode"
          label={t('qualityControl.segmentHistory.bulkStatusModal.modeLabel')}
        >
          <Radio.Group
            options={[
              {
                value: UpdateMode.complete,
                label: t(
                  'qualityControl.segmentHistory.bulkStatusModal.modeComplete',
                ),
              },
              {
                value: UpdateMode.partial,
                label: t(
                  'qualityControl.segmentHistory.bulkStatusModal.modePartial',
                ),
              },
            ]}
          />
        </Form.Item>
        <Form.Item<FormValues>
          name="newStatus"
          label={t('qualityControl.segmentHistory.bulkStatusModal.statusLabel')}
          rules={[{ required: true }]}
        >
          <Select options={statusOptions} allowClear={false} />
        </Form.Item>
        <Form.Item<FormValues>
          name="scanSiteId"
          label={t(
            'qualityControl.segmentHistory.bulkStatusModal.scanSiteLabel',
          )}
          rules={[{ required: true }]}
        >
          <Select
            options={scanSiteOptions}
            allowClear={false}
            loading={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
